import { useNavigate } from 'react-router-dom';
import { useCharacterCreationStore } from '../../stores/characterCreationStore';
import { saveCharacter } from '../../utils/characterDatabase';
import { hasCharacterData } from '../../utils/draftUtils';

interface LibraryEmptyStateProps {
  rulesetId: string;
}

/**
 * Empty state shown when the user has no characters.
 */
export function LibraryEmptyState({ rulesetId }: LibraryEmptyStateProps) {
  const navigate = useNavigate();
  const { draft, reset, setRulesetId } = useCharacterCreationStore();

  const handleCreateFirst = async () => {
    if (hasCharacterData(draft)) {
      await saveCharacter(draft);
    }
    reset();
    setRulesetId(rulesetId);
    navigate(`/${rulesetId}/create`, { state: { freshStart: true } });
  };
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-700 bg-slate-800/30 p-8 text-center">
      {/* Icon */}
      <div className="mb-4 rounded-full bg-slate-800 p-4">
        <svg
          className="h-12 w-12 text-slate-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </div>

      {/* Heading */}
      <h2 className="mb-2 text-xl font-semibold text-white">
        No Characters Yet
      </h2>

      {/* Description */}
      <p className="mb-6 max-w-md text-slate-400">
        Create your first character to begin your adventure. Your characters
        will be saved locally and available anytime you return.
      </p>

      {/* Primary Action */}
      <button
        type="button"
        onClick={handleCreateFirst}
        className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-3 font-medium text-white transition-colors hover:bg-amber-700"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Create Your First Character
      </button>

      {/* Secondary hint */}
      <p className="mt-6 text-sm text-slate-500">
        Or import an existing character using the menu
      </p>
    </div>
  );
}
